import { EmployeeType, EmployeeTypeLabel } from "../features/employees/domains/employee";

/**
 * Ciblage d'un réglage RH vers un ensemble de collaborateurs. Partagé par les règles d'heures
 * majorées et par les plages horaires prédéfinies — miroir de `models/employee_target.py`.
 *
 * Tout l'intérêt est d'être **résolu à la lecture** plutôt que recopié : un collaborateur
 * embauché après la création du réglage entre dans la cible sans réaffectation. C'est ce
 * qu'une liste d'identifiants figée ne sait pas faire, même en cochant un groupe entier.
 */
export type EmployeeTargetMode = "ALL" | "EMPLOYEE_TYPES" | "EMPLOYEES";

export interface EmployeeTarget {
  mode: EmployeeTargetMode;
  employee_types: string[];
  employee_ids: string[];
}

export function createDefaultEmployeeTarget(): EmployeeTarget {
  return { mode: "ALL", employee_types: [], employee_ids: [] };
}

/** Recopie défensive : l'API peut rendre un objet partiel, et un `mode` absent vaut « tous ». */
export function createEmployeeTargetInstance(jsonData?: Partial<EmployeeTarget> | null): EmployeeTarget {
  return {
    mode: jsonData?.mode ?? "ALL",
    employee_types: [...(jsonData?.employee_types ?? [])],
    employee_ids: [...(jsonData?.employee_ids ?? [])],
  };
}

export const EMPLOYEE_TARGET_MODE_OPTIONS: { label: string; value: EmployeeTargetMode }[] = [
  { label: "Tous les collaborateurs", value: "ALL" },
  { label: "Par catégorie", value: "EMPLOYEE_TYPES" },
  { label: "Collaborateurs spécifiques", value: "EMPLOYEES" },
];

// Fonction et non constante : évaluée au chargement du module, elle lit `EmployeeType` encore
// indéfini à travers le cycle du barrel `src/app/domains`, et toute spec qui l'importe crashe.
export function employeeTypeOptions(): { label: string; value: string }[] {
  return Object.values(EmployeeType).map((type) => ({
    label: EmployeeTypeLabel[type] ?? type,
    value: type as string,
  }));
}

/** Libellé court pour une cellule de tableau ou un résumé. */
export function employeeTargetLabel(target: EmployeeTarget | null | undefined): string {
  if (!target || target.mode === "ALL") {
    return "Tous les collaborateurs";
  }

  if (target.mode === "EMPLOYEE_TYPES") {
    const labels = target.employee_types.map((type) => EmployeeTypeLabel[type] ?? type);
    return labels.length > 0 ? labels.join(", ") : "Aucune catégorie";
  }

  const count = target.employee_ids.length;
  return count === 1 ? "1 collaborateur" : `${count} collaborateurs`;
}

/** Miroir de `target_matches`. `employeeType` est celui de la position considérée, pas un attribut
 * du collaborateur : à la création, c'est le type en cours de saisie. */
export function targetMatches(
  target: EmployeeTarget | null | undefined,
  employeeId: string | null | undefined,
  employeeType: string | null | undefined,
): boolean {
  const mode = target?.mode ?? "ALL";
  if (mode === "ALL") {
    return true;
  }
  if (mode === "EMPLOYEE_TYPES") {
    return !!employeeType && (target.employee_types ?? []).includes(employeeType);
  }
  if (mode === "EMPLOYEES") {
    return !!employeeId && (target.employee_ids ?? []).includes(employeeId);
  }

  return false;
}

/** Miroir de `target_specificity` : départage deux réglages qui visent le même collaborateur, le
 * rang 0 restant réservé à ce qui est saisi sur la fiche. */
export function targetSpecificity(target: EmployeeTarget | null | undefined): number {
  if (target?.mode === "EMPLOYEES") {
    return 1;
  }

  return target?.mode === "EMPLOYEE_TYPES" ? 2 : 3;
}

/** Message d'erreur utilisateur, ou `null` si la cible est valide. Miroir de `validate_target`. */
export function validateEmployeeTarget(target: EmployeeTarget): string | null {
  if (target.mode === "EMPLOYEE_TYPES" && target.employee_types.length === 0) {
    return "Sélectionnez au moins une catégorie de collaborateurs.";
  }
  if (target.mode === "EMPLOYEES" && target.employee_ids.length === 0) {
    return "Sélectionnez au moins un collaborateur.";
  }
  return null;
}
